import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Modal, Field, TextInput, TextArea, Toggle, PrimaryButton } from "@/components/admin/ui";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { title: "", description: "", category: "NOTICE", date: today(), image_url: "", active: true, order: 0 };
const CATEGORIES = ["NOTICE", "SYSTEM NOTICE", "AGENCY EVENT", "PAYMENT ALERT", "IMPORTANT"];

export default function ManageNotices() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const { data: items } = useQuery({ queryKey: ["admin-notices"], queryFn: async () => (await api.get("/admin/notices")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-notices"] });
    qc.invalidateQueries({ queryKey: ["notices"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const save = useMutation({
    mutationFn: async (data) => editing ? api.put(`/admin/notices/${editing}`, data) : api.post("/admin/notices", data),
    onSuccess: () => { invalidate(); setModal(false); toast.success(editing ? "Notice updated" : "Notice added"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/notices/${id}`),
    onSuccess: () => { invalidate(); toast.success("Notice deleted"); },
  });

  const openAdd = () => { setForm({ ...empty, date: today(), order: (items?.length || 0) }); setEditing(null); setModal(true); };
  const openEdit = (n) => { setForm(n); setEditing(n.id); setModal(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Notices</h1>
          <p className="mt-1 text-slate-400">Post announcements and important updates.</p>
        </div>
        <PrimaryButton onClick={openAdd} testid="add-notice-btn"><Plus className="h-5 w-5" /> Add</PrimaryButton>
      </div>

      <div className="mt-8 space-y-3">
        {(items || []).map((n) => (
          <div key={n.id} className="card-surface flex items-start gap-4 rounded-xl p-4" data-testid={`admin-notice-${n.id}`}>
            <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold/10"><Megaphone className="h-5 w-5 text-gold" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-gold/10 px-2 py-0.5 text-xs font-semibold text-gold">{n.category}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${n.active ? "bg-whatsapp/15 text-whatsapp" : "bg-slate-600/30 text-slate-400"}`}>{n.active ? "Active" : "Inactive"}</span>
                <span className="text-xs text-slate-500">{n.date}</span>
              </div>
              <p className="mt-1 font-bold text-slate-100">{n.title}</p>
              <p className="line-clamp-2 text-sm text-slate-400">{n.description}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button onClick={() => openEdit(n)} className="rounded-lg p-2 text-gold hover:bg-gold/10" data-testid={`edit-notice-${n.id}`}><Pencil className="h-5 w-5" /></button>
              <button onClick={() => { if (window.confirm("Delete this notice?")) del.mutate(n.id); }} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-notice-${n.id}`}><Trash2 className="h-5 w-5" /></button>
            </div>
          </div>
        ))}
        {items?.length === 0 && <p className="py-12 text-center text-slate-500">No notices yet.</p>}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Notice" : "Add Notice"} testid="notice-modal">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-4">
          <Field label="Title">
            <TextInput value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required testid="notice-title-input" />
          </Field>
          <Field label="Description">
            <TextArea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} testid="notice-description-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                data-testid="notice-category-input" className="h-11 w-full rounded-lg border border-gold/20 bg-obsidian px-3 text-slate-100 outline-none focus:border-gold/60">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Date">
              <TextInput type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} testid="notice-date-input" />
            </Field>
          </div>
          <ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} label="Image (optional)" testid="notice-image" />
          <div className="grid grid-cols-2 items-center gap-3">
            <Field label="Display Order">
              <TextInput type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} testid="notice-order-input" />
            </Field>
            <div className="pt-6">
              <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label={form.active ? "Active" : "Inactive"} testid="notice-active-toggle" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Cancel</button>
            <PrimaryButton type="submit" disabled={save.isPending} testid="save-notice-btn">{save.isPending ? "Saving..." : "Save"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
